import React from 'react';
import { Mail, Phone, MapPin, Code, Send } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const services = [
    { name: 'Web Development', link: '/web-development' },
    { name: 'Mobile Apps', link: '/mobile-app-development' },
    { name: 'E-commerce', link: '/ecommerce-solutions' },
    { name: 'AI Development', link: '/ai-ml-solutions' },
    { name: 'UI/UX Design', link: '/ui-ux-design' },
    { name: 'Hosting & Servers', link: '/hosting' },
    { name: 'Cloud & DevOps', link: '/cloud-devops' }
  ];

  const solutions = [
    { name: 'ERP Systems', link: '/erp-solution' },
    { name: 'CRM Systems', link: '/crm-solution' },
    { name: 'HRMS Portal', link: '/hrm-solution' },
    { name: 'Hospital Management', link: '/hospital-management' },
    { name: 'LMS Platform', link: '/lms-solution' },
    { name: 'POS Solution', link: '/pos-solution' },
    { name: 'Printing', link: '/printing-solution' }
  ];

  const company = [
    { name: 'About Us', link: '/about-us' },
    { name: 'Tech Insights & Blog', link: '/insights' },
    { name: 'Projects & Solutions', link: '/projects' },
    { name: 'Careers & Hiring', link: '/careers' },
    { name: 'FAQ & Help', link: '/faq' },
    { name: 'Contact Us', link: '/contact' }
  ];

  return (
    <footer className="relative bg-gray-950 text-left overflow-hidden">
      {/* Sleek Top Cyan & Orange Gradient Border Line */}
      <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-cyan-500 via-blue-500 via-amber-500 to-orange-500 z-20 opacity-90" />

      {/* Dark World Map Background Graphic & Overlays */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/dark_world_map_footer_bg.png"
          alt="Global Telemetry World Map Background"
          className="w-full h-full object-cover filter brightness-110 contrast-115 opacity-80 scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-gray-950/40 to-gray-950/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-transparent to-gray-950/80" />
      </div>

      <div className="container mx-auto px-6 relative z-10">

        {/* Main Footer Grid - Compact Height */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 py-10">

          {/* Brand Info */}
          <div className="lg:col-span-2 space-y-3">
            <Link to="/" className="flex items-center space-x-2.5 group">
              <img
                src="/images/logohoasp_white.png"
                alt="HOASP International"
                className="h-10 md:h-12 w-auto object-contain"
              />
            </Link>

            <p className="text-gray-400 text-base leading-relaxed max-w-sm">
              Full-service web engineering agency crafting scalable web platforms, mobile apps, and enterprise AI software.
            </p>

            <div className="space-y-1.5 text-base text-gray-400 pt-1">
              <div className="flex items-center space-x-2">
                <Mail className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                <span>info@hoasp.com</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                <span>+91  8796 118 101</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                <span>K1, mahindra Enclave, Ghaziabad UP</span>
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-3 border-l-2 border-orange-500 pl-2.5">
              Services
            </h4>
            <ul className="space-y-2 text-base">
              {services.map((service, index) => (
                <li key={index}>
                  <Link to={service.link} className="text-gray-400 hover:text-orange-400 transition-colors">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Column */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-3 border-l-2 border-cyan-400 pl-2.5">
              Solutions
            </h4>
            <ul className="space-y-2 text-base">
              {solutions.map((solution, index) => (
                <li key={index}>
                  <Link to={solution.link} className="text-gray-400 hover:text-cyan-400 transition-colors">
                    {solution.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-white font-bold text-base uppercase tracking-wider mb-3 border-l-2 border-amber-400 pl-2.5">
              Company
            </h4>
            <ul className="space-y-2 text-base">
              {company.map((item, index) => (
                <li key={index}>
                  <Link to={item.link} className="text-gray-400 hover:text-white transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Right-Aligned Compact Newsletter Bar */}
        <div className="flex justify-end mb-4 md:pr-16 lg:pr-20">
          <div className="py-2.5 px-4 bg-gray-900/80 border border-gray-800 rounded-2xl flex items-center space-x-3 max-w-md w-full sm:w-auto">
            <div className="flex items-center space-x-2 text-left flex-shrink-0">
              <Mail className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-bold text-white whitespace-nowrap">Subscribe:</span>
            </div>

            <form onSubmit={(e) => e.preventDefault()} className="flex items-center space-x-2 flex-1">
              <input
                type="email"
                placeholder="Work email..."
                required
                className="bg-gray-950 border border-gray-800 rounded-xl px-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-400 w-full sm:w-44"
              />
              <button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-colors flex items-center gap-1 flex-shrink-0 shadow-md"
              >
                <span>Join</span>
                <Send className="w-3 h-3" />
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Copyright & Legal Links - Centered */}
        <div className="border-t border-gray-800/80 py-5 flex flex-col items-center justify-center text-center gap-2 text-xs text-gray-400">
          <p>© {new Date().getFullYear()} HOASP International. All rights reserved.</p>

          <div className="flex items-center space-x-3 text-xs">
            <Link to="/privacy-policy" className="hover:text-orange-400 transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms-of-service" className="hover:text-orange-400 transition-colors">Terms of Service</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;